import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import fetch from './fetch';

/**
 * useNewsFeedList
 * @param {{ pageSize?: number, keyword?: string, params?: Record<string, any>, enabled?: boolean }} args
 */
function useNewsFeedList(args) {
  const { cb, key } = fetch(args);

  const {
    data,
    isInitialLoading,
    refetch,
    isRefetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(key, cb, {
    staleTime: 60_000,
    cacheTime: 60_000,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + (page?.results?.length || 0), 0);
      const count = lastPage?.count ?? 0;
      return totalFetched < count ? allPages.length + 1 : undefined;
    },
    select: (_data) => _data.pages?.flatMap((page) => page?.results || []),
    enabled: args?.enabled !== undefined ? !!args.enabled : true,
  });

  const fetchNext = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, isFetchingNextPage, hasNextPage]);

  return {
    data: data || [],
    isInitialLoading,
    refetch,
    isRefetching,
    fetchNextPage: fetchNext,
    isFetchingNextPage,
    hasNextPage,
  };
}

/**
 * useSearchNewsMutation: trigger a fresh search and warm the cache for page 1
 * @param {{ pageSize?: number, params?: Record<string, any> }} options
 */
function useSearchNewsMutation(options) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ keyword }) => {
      const { cb } = fetch({ pageSize: options?.pageSize || 20, keyword, params: options?.params });
      // directly call page 1
      const data = await cb({ pageParam: 1 });
      return { keyword, data };
    },
    onSuccess: ({ keyword, data }) => {
      const { key } = fetch({
        pageSize: options?.pageSize || 20,
        keyword,
        params: options?.params,
      });
      // hydrate cache for infinite query structure
      queryClient.setQueryData(key, {
        pageParams: [1],
        pages: [data],
      });
    },
  });
}

export default useNewsFeedList;
export { useSearchNewsMutation };
